import { faCirclePlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FieldArray, FormikProps, getIn, useFormikContext } from "formik";
import React from "react";
import { Button, Card, Col, Row } from "react-bootstrap";

import {
  AgriculturalOwnershipStatus,
  AgriculturalWaterSource
} from "../../../../config/filingConstants";
import { IAgriculturalIncomes } from "../../../../config/interfaces";
import FASvgIcon from "../../../FASvgIcon";
import { NumericInputField, SelectInputField, TextInputField } from "../../../fields";
import PreventEditComponent from "../../../PreventEditComponent";

const OIAgricultureLandDetailsForm = () => {
  const { values } = useFormikContext<IAgriculturalIncomes>();

  return (
    <>
      <div className="section-title-wrap">
        <h5>Agriculture Land Details</h5>
      </div>
      <Card className="more-info-card shadow-none">
        <Card.Body>
          <section>
            <FieldArray name="AgricultureLandDetails">
              {({ push, remove, form }) => (
                <>
                  {values?.AgricultureLandDetails?.length > 0
                    ? values?.AgricultureLandDetails?.map((_, index) => (
                        <section className="mb-3 mb-md-0" key={`agri-${index}`}>
                          <AgriculturalForm
                            index={index}
                            form={form}
                            onRemove={(itemIdx: number) => remove(itemIdx)}
                          />
                        </section>
                      ))
                    : null}
                  <PreventEditComponent>
                    <Button
                      variant="link"
                      className="btn-add-link"
                      onClick={() =>
                        push({
                          DistrictName: "",
                          Pincode: "",
                          Measurement: "",
                          OwnershipStatus: "",
                          WaterSource: ""
                        })
                      }
                    >
                      <FASvgIcon icon={faCirclePlus} />
                      Add more
                    </Button>
                  </PreventEditComponent>
                </>
              )}
            </FieldArray>
          </section>
        </Card.Body>
      </Card>
    </>
  );
};

export default OIAgricultureLandDetailsForm;

type ALFormProps = {
  index: number;
  form: FormikProps<IAgriculturalIncomes>;
  onRemove: (itemIdx: number) => void;
};

const AgriculturalForm: React.FC<ALFormProps> = (props: ALFormProps) => {
  const { index, onRemove, form } = props;

  const { setFieldValue, handleBlur, values, touched, errors } = form;

  return (
    <>
      <Row className="align-items-center">
        <Col sm={6} md={4} xl>
          <TextInputField
            label="District"
            name={`AgricultureLandDetails.${index}.DistrictName`}
            value={values.AgricultureLandDetails[index].DistrictName}
            onChange={(value) =>
              setFieldValue(`AgricultureLandDetails.${index}.DistrictName`, value)
            }
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue(
                `AgricultureLandDetails.${index}.DistrictName`,
                values.AgricultureLandDetails[index].DistrictName.trim()
              );
            }}
            isInvalid={
              Boolean(getIn(errors, `AgricultureLandDetails.${index}.DistrictName`)) &&
              getIn(touched, `AgricultureLandDetails.${index}.DistrictName`)
            }
            errorMsg={getIn(errors, `AgricultureLandDetails.${index}.DistrictName`)}
          />
        </Col>
        <Col sm={6} md={4} xl>
          <NumericInputField
            label="Pincode"
            name={`AgricultureLandDetails.${index}.Pincode`}
            value={values?.AgricultureLandDetails[index].Pincode}
            onBlur={handleBlur(`AgricultureLandDetails.${index}.Pincode`)}
            onChange={(value) => setFieldValue(`AgricultureLandDetails.${index}.Pincode`, value)}
            isInvalid={
              Boolean(getIn(errors, `AgricultureLandDetails.${index}.Pincode`)) &&
              getIn(touched, `AgricultureLandDetails.${index}.Pincode`)
            }
            errorMsg={getIn(errors, `AgricultureLandDetails.${index}.Pincode`)}
            decimalUpto={0}
            separator=""
          />
        </Col>
        <Col sm={6} md={4} xl>
          <NumericInputField
            label="Size (in acres)"
            name={`AgricultureLandDetails.${index}.Measurement`}
            value={values?.AgricultureLandDetails[index].Measurement}
            onBlur={handleBlur(`AgricultureLandDetails.${index}.Measurement`)}
            onChange={(value) =>
              setFieldValue(`AgricultureLandDetails.${index}.Measurement`, value)
            }
            isInvalid={
              Boolean(getIn(errors, `AgricultureLandDetails.${index}.Measurement`)) &&
              getIn(touched, `AgricultureLandDetails.${index}.Measurement`)
            }
            errorMsg={getIn(errors, `AgricultureLandDetails.${index}.Measurement`)}
          />
        </Col>
        <Col sm={6} md={6} xl>
          <SelectInputField
            label="Ownership Status"
            placeholder="Select status"
            isOptionArrayOfObject={false}
            name={`AgricultureLandDetails.${index}.OwnershipStatus`}
            options={AgriculturalOwnershipStatus}
            value={[values?.AgricultureLandDetails[index].OwnershipStatus]}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setFieldValue(
                  `AgricultureLandDetails.${index}.OwnershipStatus`,
                  selectedOption.value
                );
              } else {
                setFieldValue(`AgricultureLandDetails.${index}.OwnershipStatus`, undefined);
              }
            }}
            onBlur={handleBlur(`AgricultureLandDetails.${index}.OwnershipStatus`)}
            isInvalid={
              Boolean(getIn(errors, `AgricultureLandDetails.${index}.OwnershipStatus`)) &&
              getIn(touched, `AgricultureLandDetails.${index}.OwnershipStatus`)
            }
            errorMsg={getIn(errors, `AgricultureLandDetails.${index}.OwnershipStatus`)}
          />
        </Col>
        <Col sm md xl>
          <SelectInputField
            label="Water Source"
            placeholder="Select source"
            isOptionArrayOfObject={false}
            name={`AgricultureLandDetails.${index}.WaterSource`}
            options={AgriculturalWaterSource}
            value={[values.AgricultureLandDetails[index].WaterSource]}
            onChange={(selectedOption) => {
              if (selectedOption) {
                setFieldValue(`AgricultureLandDetails.${index}.WaterSource`, selectedOption.value);
              } else {
                setFieldValue(`AgricultureLandDetails.${index}.WaterSource`, undefined);
              }
            }}
            onBlur={handleBlur(`AgricultureLandDetails.${index}.WaterSource`)}
            isInvalid={
              Boolean(getIn(errors, `AgricultureLandDetails.${index}.WaterSource`)) &&
              getIn(touched, `AgricultureLandDetails.${index}.WaterSource`)
            }
            errorMsg={getIn(errors, `AgricultureLandDetails.${index}.WaterSource`)}
          />
        </Col>
        <PreventEditComponent>
          <Col sm={"auto"} className="ps-0">
            <Button
              variant="transparent"
              className="text-greyDark icon-only d-none d-sm-flex"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
            </Button>
            <Button
              variant="transparent"
              className="btn-delete-full d-sm-none"
              onClick={() => onRemove(index)}
            >
              <FASvgIcon icon={faTrash} />
              <span>Delete</span>
            </Button>
          </Col>
        </PreventEditComponent>
      </Row>
      {index !== values.AgricultureLandDetails.length - 1 && (
        <hr className="d-block d-xl-none my-4" />
      )}
    </>
  );
};
